import { Language, MentionSourceType, SOURCE, Source } from "@/constants";
import { SearchQuery, UnsavedQuery } from "@/types/SearchQuery";

export const PROP_TO_PARAM_MAP = {
  uuid: "uuid",
  keywords: "keywords",
  languages: "languages",
  sources: "sources",
  tags: "tags",
  nearKeywords: "near_keywords",
  nearDistance: "near_distance",
  nearNearKeywords: "near_near_keywords",
  notNearKeywords: "not_near_keywords",
  notNearDistance: "not_near_distance",
  includeChannelNames: "include_channel_names",
  excludeChannelNames: "exclude_channel_names",
  minReachByChannelType: "min_reach_by_channel_type",
  excludeTopics: "exclude_topics",
  mentionTypes: "mention_types",
  includePaywalled: "include_paywalled",
} as const;

export type FilterKey = keyof typeof PROP_TO_PARAM_MAP;
type SnakeKey = (typeof PROP_TO_PARAM_MAP)[FilterKey];

export const MIN_REACH_BY_CHANNEL_TYPE_SOURCES = [
  SOURCE.YOUTUBE,
  SOURCE.INSTAGRAM,
  SOURCE.TIKTOK,
  SOURCE.NEWS,
  SOURCE.TWITTER,
  SOURCE.FACEBOOK,
];

export type MinReachByChannelTypeSource =
  (typeof MIN_REACH_BY_CHANNEL_TYPE_SOURCES)[number];

export type MinReachByChannelTypeValues = {
  [SOURCE.YOUTUBE]?: number;
  [SOURCE.INSTAGRAM]?: number;
  [SOURCE.TIKTOK]?: number;
  [SOURCE.NEWS]?: number;
  [SOURCE.TWITTER]?: number;
  [SOURCE.FACEBOOK]?: number;
  has_custom_values: boolean;
};

const MIN_REACH_BY_CHANNEL_TYPE_DEFAULT_VALUES: MinReachByChannelTypeValues = {
  [SOURCE.YOUTUBE]: 1000,
  [SOURCE.INSTAGRAM]: 400,
  [SOURCE.TIKTOK]: 0,
  [SOURCE.NEWS]: 0,
  [SOURCE.TWITTER]: 0,
  [SOURCE.FACEBOOK]: 0,
  has_custom_values: false,
};

export const getMinReachByChannelTypeDefaultValues = (
  minReachByChannelTypeSources: MinReachByChannelTypeSource[]
): MinReachByChannelTypeValues => {
  const defaultValues: Partial<MinReachByChannelTypeValues> = {
    has_custom_values: false,
  };

  minReachByChannelTypeSources.forEach((source) => {
    defaultValues[source] = MIN_REACH_BY_CHANNEL_TYPE_DEFAULT_VALUES[source];
  });

  return defaultValues as MinReachByChannelTypeValues;
};

type MinReachByChannelTypeNoValues = { no_values: true };

type MinReachByChannelType =
  MinReachByChannelTypeValues | MinReachByChannelTypeNoValues;

export type QueryIn = {
  uuid?: string | null;
  keywords?: string[];
  languages?: Language[];
  sources?: Source[];
  tags?: string[];
  nearKeywords?: string[];
  nearDistance?: number;
  nearNearKeywords?: string[];
  notNearKeywords?: string[];
  notNearDistance?: number;
  includeChannelNames?: (string | null | undefined)[];
  excludeChannelNames?: (string | null | undefined)[];
  minReachByChannelType?: MinReachByChannelType;
  excludeTopics?: string[];
  mentionTypes?: MentionSourceType;
  includePaywalled?: boolean;
};

type CamelToSnake<T extends object> = {
  [
    K in keyof T as K extends FilterKey ? (typeof PROP_TO_PARAM_MAP)[K] : never
  ]?: T[K];
};

export type QueryOut = CamelToSnake<QueryIn>;

// Fills every UnsavedQuery field, defaulting missing ones to null. Required because
// Nullablefy makes UnsavedQuery's keys required-but-nullable; deleteNil at the API
// boundary strips them again before submission.
export const padUnsavedQuery = (q: Partial<SearchQuery>): UnsavedQuery => ({
  keywords: q.keywords ?? [],
  languages: q.languages ?? null,
  sources: q.sources ?? null,
  tags: q.tags ?? null,
  near_keywords: q.near_keywords ?? null,
  near_near_keywords: q.near_near_keywords ?? null,
  not_near_keywords: q.not_near_keywords ?? null,
  near_distance: q.near_distance ?? null,
  not_near_distance: q.not_near_distance ?? null,
  include_channel_names: q.include_channel_names ?? null,
  exclude_channel_names: q.exclude_channel_names ?? null,
  min_reach_by_channel_type: q.min_reach_by_channel_type ?? null,
  mention_types: q.mention_types ?? null,
  exclude_topics: q.exclude_topics ?? null,
  include_paywalled: q.include_paywalled ?? null,
});

export const queryToFormValues = (q: {
  [K in SnakeKey]?: SearchQuery[K] | null;
}): QueryIn => {
  const out: QueryIn = {};
  (Object.entries(PROP_TO_PARAM_MAP) as [FilterKey, SnakeKey][]).forEach(
    ([camel, snake]) => {
      const v = q[snake];
      if (v != null) {
        // @ts-expect-error - index remapping handled in type CamelToSnake
        out[camel] = v;
      }
    }
  );
  return out;
};

export const prepareQueryForCreate = (query: Partial<QueryIn>): SearchQuery => {
  const out = {} as SearchQuery;

  (Object.entries(PROP_TO_PARAM_MAP) as [FilterKey, SnakeKey][]).forEach(
    ([camel, snake]) => {
      const v = query[camel];
      if (v !== undefined && v !== null && v !== "") {
        // @ts-expect-error - index remapping handled in type CamelToSnake
        out[snake] = v;
      }
    }
  );

  const exclude = (query.excludeChannelNames ?? []).filter(Boolean) as string[];
  const include = (query.includeChannelNames ?? []).filter(Boolean) as string[];

  out[PROP_TO_PARAM_MAP.excludeChannelNames] = exclude;
  out[PROP_TO_PARAM_MAP.includeChannelNames] = include;

  return out;
};
